import React, { useState } from 'react';
import { Mail, Phone, Building2, User, Save, X, FileEdit as Edit2, Globe, Factory, Users as Users2, MapPin } from 'lucide-react';

interface ClientInfoCardProps {
  client: {
    id: string;
    name: string;
    company_name: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
    sector: string | null;
    company_size: string | null;
    location: string | null;
  };
  onUpdate: (field: string, value: string) => Promise<void>;
}

interface EditingField {
  field: string;
  value: string;
}

const ClientInfoCard: React.FC<ClientInfoCardProps> = ({ client, onUpdate }) => {
  const [editing, setEditing] = useState<EditingField | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fields = [
    { key: 'name', label: 'Nom du contact', icon: User, value: client.name },
    { key: 'company_name', label: 'Entreprise', icon: Building2, value: client.company_name },
    { key: 'email', label: 'Email', icon: Mail, value: client.email },
    { key: 'phone', label: 'Téléphone', icon: Phone, value: client.phone },
    { key: 'website', label: 'Site web', icon: Globe, value: client.website },
    { key: 'sector', label: 'Secteur', icon: Factory, value: client.sector },
    { key: 'company_size', label: 'Taille', icon: Users2, value: client.company_size },
    { key: 'location', label: 'Localisation', icon: MapPin, value: client.location },
  ];

  const handleEdit = (field: string, currentValue: string | null) => {
    setEditing({ field, value: currentValue || '' });
  };

  const handleCancel = () => {
    setEditing(null);
  };

  const handleSave = async () => {
    if (!editing) return;

    try {
      setSaving(true);
      await onUpdate(editing.field, editing.value);
      setEditing(null);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving field:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (value: string) => {
    if (editing) {
      setEditing({ ...editing, value });
      setHasChanges(true);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations société</h3>

      <div className="space-y-4">
        {fields.map((field) => {
          const Icon = field.icon;
          const isEditing = editing?.field === field.key;
          const displayValue = field.value || '';

          return (
            <div key={field.key} className="flex items-start gap-3">
              <Icon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />

              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-500 mb-1">
                  {field.label}
                </div>

                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editing.value}
                      onChange={(e) => handleChange(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSave();
                        if (e.key === 'Escape') handleCancel();
                      }}
                    />
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Sauvegarder"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleCancel}
                      className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      title="Annuler"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => handleEdit(field.key, field.value)}
                    className="group flex items-center gap-2 cursor-pointer"
                  >
                    {displayValue ? (
                      <span className="text-gray-900">{displayValue}</span>
                    ) : (
                      <span className="text-gray-400 italic">Non renseigné</span>
                    )}
                    <Edit2 className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientInfoCard;
